/**
 * TechHub > Services (Bill, 2026-10-23): the list a client's services are picked from, per company,
 * so they're spelled the same everywhere. Each service has a brand name, a short description, a page on
 * the company website and a logo, shown on the client's Services tab (approved mockup
 * outputs/clienthub-services-mockup.html). Company admins add, edit and retire; nothing is deleted,
 * because clients may use a service (a retired one stays on those clients).
 */
import { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api, putFile, type ChService } from "../../lib/api";
import { useStore } from "../../lib/store";
import { coName } from "../../lib/brand";
import { Loading, Tile } from "../../components/bits";
import { PartTag } from "./SettingsLayout";

export function ServicesSettingsPage() {
  const { p, toast } = useStore();
  const codes = p.chCodes.filter((c) => p.ch(c).sensitive);
  const [sp, setSp] = useSearchParams();
  const co = codes.includes(sp.get("co") ?? "") ? sp.get("co")! : codes[0];
  const [list, setList] = useState<ChService[] | null>(null);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { if (co) setList((await api.chServices(co)).services); };
  useEffect(() => { setList(null); setEditing(null); load().catch((e: Error) => toast(e.message)); }, [co]);
  if (!co) return <Navigate to="/settings" replace />;

  const run = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    try { await fn(); toast(msg); await load(); return true; } catch (e) { toast((e as Error).message); return false; } finally { setBusy(false); }
  };
  const add = () => run(async () => { const r = await api.chAddService(co, name.trim()); setName(""); setEditing(r.id); }, `${name.trim()} added. Give it a description and link.`);
  const active = list?.filter((s) => s.active) ?? [];
  const retired = list?.filter((s) => !s.active) ?? [];

  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="clienthub" />
        <h1 className="title">Services</h1>
        <div className="lede">The services you pick on a client, with the brand name, a short description and a link to the service's page on your website. Every client page shows them the same way. Each company has its own list.</div>
      </div></div>
      {codes.length > 1 && (
        <div className="ctabs" role="tablist">
          {codes.map((c) => (
            <button key={c} className={`ctab ${c === co ? "on" : ""}`} role="tab" aria-selected={c === co} onClick={() => setSp({ co: c }, { replace: true })}>
              <Tile code={c} /><span>{coName(c)}</span>
            </button>
          ))}
        </div>
      )}
      <div className="card sect">
        <h3>{coName(co)} services</h3>
        <p className="cap">Changes show on every client that has the service. Retiring hides it from new picks; clients that have it keep it.</p>
        {!list ? <Loading /> : (
          <>
            <ul className="svc-list">
              {active.map((s) => editing === s.id
                ? <li key={s.id} className="svc-edit"><EditService s={s} busy={busy} onCancel={() => setEditing(null)}
                    onSave={async (b) => { if (await run(() => api.chUpdateService(s.id, b), "Saved.")) setEditing(null); }} onLogo={load} /></li>
                : (
                <li key={s.id}>
                  {s.logo_url ? <span className="svc-thumb"><img src={s.logo_url} alt="" /></span> : <span className="svc-thumb" aria-hidden="true" />}
                  <div className="svc-row">
                    <b>{s.name}{s.brand && <span className="svc-brand">{s.brand}</span>}</b>
                    <span className="svc-desc">{s.description || "No description yet."}</span>
                    {s.url && <a className="svc-link" href={s.url} target="_blank" rel="noreferrer">{s.url.replace(/^https?:\/\//, "")}</a>}
                  </div>
                  <span className="cap">{s.clients === 1 ? "1 client" : `${s.clients} clients`}</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditing(s.id)}>Edit</button>
                  <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void run(() => api.chUpdateService(s.id, { active: false }), `${s.name} retired.`)}>Retire</button>
                </li>
              ))}
              {!active.length && <li className="cap">No services yet. Add the first one below.</li>}
            </ul>
            <div className="form-row" style={{ marginTop: 14, alignItems: "flex-end" }}>
              <label className="grow"><span className="lbl">Add a service</span>
                <input className="inp" value={name} maxLength={60} placeholder="e.g. Dark Web Monitoring" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && name.trim().length >= 2) void add(); }} />
              </label>
              <button className="btn btn-accent" disabled={busy || name.trim().length < 2} onClick={() => void add()}>Add service</button>
            </div>
            {retired.length > 0 && (
              <>
                <h4 className="svc-sub">Retired</h4>
                <ul className="svc-list retired">
                  {retired.map((s) => (
                    <li key={s.id}>
                      <div className="svc-row"><b>{s.name}</b></div>
                      <span className="cap">{s.clients === 1 ? "Still on 1 client" : s.clients ? `Still on ${s.clients} clients` : "Not on any client"}</span>
                      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void run(() => api.chUpdateService(s.id, { active: true }), `${s.name} restored.`)}>Restore</button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function EditService({ s, busy, onSave, onCancel, onLogo }: { s: ChService; busy: boolean; onSave: (b: { name: string; brand: string; description: string; url: string }) => void; onCancel: () => void; onLogo: () => Promise<void> }) {
  const { toast } = useStore();
  const [f, setF] = useState({ name: s.name, brand: s.brand, description: s.description, url: s.url });
  const [logo, setLogo] = useState(s.logo_url);
  const [up, setUp] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const upload = async (fl: File) => {
    if (!["image/png", "image/jpeg", "image/webp"].includes(fl.type)) return toast("Logos can be PNG, JPEG or WebP.");
    if (fl.size > 512 * 1024) return toast("Logos are limited to 512 KB.");
    setUp(true);
    try {
      const { upload: u } = await api.chServiceLogoUpload(s.id, fl.type, fl.size);
      await putFile(u.url, fl);
      setLogo((await api.chSetServiceLogo(s.id, u.key)).logo_url);
      toast("Logo saved."); await onLogo();
    } catch (e) { toast((e as Error).message); } finally { setUp(false); if (file.current) file.current.value = ""; }
  };
  const removeLogo = async () => {
    try { await api.chRemoveServiceLogo(s.id); setLogo(null); toast("Logo removed."); await onLogo(); } catch (e) { toast((e as Error).message); }
  };
  const ok = f.name.trim().length >= 2;
  return (
    <div className="svc-form">
      <div className="form-row">
        <label className="grow"><span className="lbl">Service name</span><input className="inp" value={f.name} maxLength={60} onChange={set("name")} /></label>
        <label className="grow"><span className="lbl">Brand name <span className="help-in">optional</span></span><input className="inp" value={f.brand} maxLength={40} onChange={set("brand")} placeholder="e.g. SecurityAssurance℠" /></label>
      </div>
      <div className="field"><span className="lbl">Logo <span className="help-in">optional, PNG, JPEG or WebP up to 512 KB; shown in the service's box on the client page</span></span>
        <div className="svc-logo-edit">
          <span className="svc-logo">{logo ? <img src={logo} alt="" /> : <span className="cap">No logo</span>}</span>
          <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { const fl = e.target.files?.[0]; if (fl) void upload(fl); }} />
          <button className="btn btn-ghost btn-sm" disabled={up} onClick={() => file.current?.click()}>{up ? "Uploading…" : logo ? "Replace logo" : "Upload logo"}</button>
          {logo && <button className="btn btn-ghost btn-sm" onClick={() => void removeLogo()}>Remove</button>}
        </div>
      </div>
      <label className="field"><span className="lbl">Description <span className="help-in">one or two lines, shown on every client that has it</span></span>
        <textarea className="inp" rows={2} value={f.description} maxLength={300} onChange={set("description")} /></label>
      <label className="field"><span className="lbl">Page on your website <span className="help-in">optional, shown as Learn more</span></span>
        <input className="inp" value={f.url} maxLength={300} onChange={set("url")} placeholder="https://" /></label>
      <div className="row-btns">
        <button className="btn btn-accent btn-sm" disabled={busy || !ok} onClick={() => onSave({ name: f.name.trim(), brand: f.brand.trim(), description: f.description.trim(), url: f.url.trim() })}>Save</button>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <span className="cap" style={{ marginLeft: "auto" }}>{s.clients === 1 ? "1 client" : `${s.clients} clients`}</span>
      </div>
    </div>
  );
}
